/**
 * Brand Modern – same functionality as before, but renders DOM that matches
 * the default collection template structure so it inherits all theme styles.
 *
 * Family sidebar  → facet-checkbox / accordion-details classes
 * Printer cards   → product-card / product-card__* classes
 * Grid            → f-grid / f-column / products-list classes
 */
(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────────── */

  function getCatalogUrl(brandHandle) {
    const base = window.catalogAssetUrl || '';
    const filename = `catalog-${brandHandle.toLowerCase().replace(/\s+/g, '-')}.json`;
    if (base) {
      return base.split('?')[0].replace(/catalog-index\.json$/i, filename);
    }
    return `/assets/${filename}`;
  }

  function processBrandData(brandData) {
    const families = [];
    const models = [];
    if (!brandData || !brandData.families) return { families, models };

    brandData.families.forEach((family) => {
      const title = family.title || family.name;
      const modelCount = family.modelCount || (family.models && family.models.length) || 0;
      families.push({ title, handle: family.handle, modelCount });

      if (Array.isArray(family.models)) {
        family.models.forEach((model) => {
          models.push({
            title: model.name,
            handle: model.handle,
            familyTitle: title,
            url: `/collections/${model.handle}`,
            image: model.image || null,
            productsCount: model.productsCount || 0,
          });
        });
      }
    });

    return { families, models };
  }

  function escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = String(s || '');
    return div.innerHTML;
  }

  const PLACEHOLDER_IMG_URL = window.__comingSoonPlaceholder || 'https://cdn.shopify.com/s/files/1/0896/6596/2355/collections/Brother_DCP1512.jpg?v=1769636782';

  /* Minimal printer placeholder that won't conflict with theme placeholder-svg styles */
  const PLACEHOLDER_SVG = `<svg class="placeholder-svg" viewBox="0 0 525 525"
    xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <rect width="525" height="525" fill="#f4f4f4"/>
    <rect x="100" y="130" width="325" height="220" rx="8" fill="#e8e8e8"/>
    <rect x="162" y="310" width="200" height="24" rx="4" fill="#d5d5d5"/>
  </svg>`;

  /* ── Init ─────────────────────────────────────────────────── */

  function init() {
    const section = document.querySelector('.collection[data-section-id]');
    if (!section) return;

    const sectionId    = section.dataset.sectionId;
    const brandHandle  = section.dataset.brandHandle || '';
    const initialCount = parseInt(section.dataset.initialCount, 10) || 24;
    const loadMoreCount = parseInt(section.dataset.loadMoreCount, 10) || 24;
    const pcardStyle = section.dataset.pcardStyle || 'card';
    const pcardColorScheme = section.dataset.pcardColorScheme || '';
    const pcardContentAlignment = section.dataset.pcardContentAlignment || 'left';
    const pcardTitleLineLimit = section.dataset.pcardTitleLineLimit || '2';

    const familyListEl = document.getElementById(`brand-modern-families-${sectionId}`);
    const gridEl       = document.getElementById('ProductsList');
    const countEl      = document.getElementById(`brand-modern-count-${sectionId}`);
    const searchInput  = document.getElementById(`brand-modern-search-${sectionId}`);
    const sortSelect   = document.getElementById(`brand-modern-sort-${sectionId}`);
    const sentinel     = document.getElementById(`brand-modern-sentinel-${sectionId}`);
    const emptyEl      = document.getElementById(`brand-modern-empty-${sectionId}`);
    const viewButtons  = section.querySelectorAll('[data-bm-view]');

    if (!familyListEl || !gridEl) return;

    let allFamilies        = [];
    let allModels          = [];
    const selectedFamilies = new Set(); // empty = All
    let searchQuery        = '';
    let sortMode           = 'relevance';
    let shownCount         = 0;
    let loadingMore        = false;
    let isListView         = false;
    let onlyWithProducts   = false;

    /* ── Filter / sort ─────────────────────────────────────── */

    function getFilteredModels() {
      const filtered = allModels.filter((m) => {
        const matchFamily = selectedFamilies.size === 0 || selectedFamilies.has(m.familyTitle);
        const q = (searchQuery || '').toLowerCase().trim();
        const matchSearch = !q || (m.title && m.title.toLowerCase().includes(q));
        const matchProducts = !onlyWithProducts || m.productsCount > 0;
        return matchFamily && matchSearch && matchProducts;
      });

      if (sortMode === 'name-asc') {
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      } else if (sortMode === 'name-desc') {
        filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      }

      return filtered;
    }

    /* ── Family sidebar ────────────────────────────────────── */

    function getFamilyCounts() {
      var counts = {};
      var total = 0;
      allModels.forEach(function (m) {
        if (onlyWithProducts && m.productsCount <= 0) return;
        counts[m.familyTitle] = (counts[m.familyTitle] || 0) + 1;
        total++;
      });
      counts.__total = total;
      return counts;
    }

    function renderFamilyList() {
      familyListEl.innerHTML = '';
      var counts = getFamilyCounts();

      appendFamilyItem(
        `bm-all-${sectionId}`,
        'All',
        counts.__total,
        selectedFamilies.size === 0,
        function () {
          selectedFamilies.clear();
          searchQuery = '';
          if (searchInput) searchInput.value = '';
          shownCount = 0;
          renderFamilyList();
          renderGrid();
          updateCount();
        }
      );

      allFamilies.forEach((f, i) => {
        var familyCount = counts[f.title] || 0;
        if (onlyWithProducts && familyCount === 0) return;

        appendFamilyItem(
          `bm-fam-${sectionId}-${i}`,
          f.title,
          familyCount,
          selectedFamilies.has(f.title),
          function (checked) {
            if (checked) {
              selectedFamilies.add(f.title);
            } else {
              selectedFamilies.delete(f.title);
            }
            shownCount = 0;
            renderFamilyList();
            renderGrid();
            updateCount();
          }
        );
      });
    }

    /**
     * Renders a single facet-style checkbox row that matches the default
     * facets.liquid markup so it inherits all theme facet styles.
     */
    function appendFamilyItem(id, label, count, checked, onChange) {
      const li = document.createElement('li');
      li.className = 'flex items-center gap-2';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'facet-checkbox__input';
      input.id = id;
      input.checked = checked;
      input.addEventListener('change', function () { onChange(this.checked); });

      const lbl = document.createElement('label');
      lbl.htmlFor = id;
      lbl.className = 'flex items-center gap-2 reversed-link w-full cursor-pointer';
      lbl.innerHTML = `
        <div class="flex flex-grow justify-between items-center gap-1 text-subtext">
          <span class="reversed-link__text">${escapeHtml(label)}</span>
          <span class="count">${count}</span>
        </div>`;

      li.appendChild(input);
      li.appendChild(lbl);
      familyListEl.appendChild(li);
    }

    /* ── Product grid ──────────────────────────────────────── */

    function renderGrid(append) {
      const filtered = getFilteredModels();

      if (!append) {
        shownCount = Math.min(initialCount, filtered.length);
        gridEl.innerHTML = '';
        gridEl.classList.toggle('products-list--list-card', isListView);
        gridEl.setAttribute('data-layout', isListView ? 'list' : 'grid');
      }

      if (filtered.length === 0 && !append) {
        if (emptyEl) emptyEl.style.display = '';
        if (sentinel) sentinel.style.display = 'none';
        updateCount();
        return;
      }
      if (emptyEl) emptyEl.style.display = 'none';

      const start = append ? shownCount : 0;
      const end   = Math.min(start + (append ? loadMoreCount : shownCount), filtered.length);

      filtered.slice(start, end).forEach((model) => {
        gridEl.appendChild(createModelCard(model));
      });

      if (append) shownCount = end;
      if (sentinel) sentinel.style.display = shownCount < filtered.length ? 'block' : 'none';
      updateCount();
    }

    /**
     * Builds a card that uses the exact same classes as card-product.liquid
     * (product-card, product-card__wrapper, product-card__image-wrapper, etc.)
     * so it automatically picks up all pcard styles from the theme stylesheet.
     */
    function createModelCard(model) {
      const col = document.createElement('div');
      col.className = 'f-column product-column';

      const card = document.createElement('div');
      let cardClass = `product-card product-card-style-${pcardStyle}`;
      if (pcardStyle === 'card' && pcardColorScheme) {
        cardClass += ` color-${pcardColorScheme}`;
      }
      card.className = cardClass;

      const wrapper = document.createElement('div');
      wrapper.className = 'product-card__wrapper h-full';

      /* Image area */
      const imgWrapper = document.createElement('div');
      imgWrapper.className =
        'product-card__image-wrapper product-card__image-wrapper--main-only bg-none';

      const imgLink = document.createElement('a');
      imgLink.href = model.url;
      imgLink.setAttribute('aria-label', model.title);
      imgLink.setAttribute('tabindex', '-1');

      const mediaDiv = document.createElement('div');
      mediaDiv.className = 'media-wrapper product-card__image product-card__image--main';
      mediaDiv.style.setProperty('--aspect-ratio', '1');

      var imgSrc = model.image || PLACEHOLDER_IMG_URL;
      var img = document.createElement('img');
      img.src    = imgSrc;
      img.alt    = '';
      img.width  = 300;
      img.height = 300;
      img.loading = 'lazy';
      img.className = 'motion-reduce';
      img.onerror = function () {
        this.onerror = null;
        mediaDiv.innerHTML = PLACEHOLDER_SVG;
      };
      mediaDiv.appendChild(img);

      imgLink.appendChild(mediaDiv);
      imgWrapper.appendChild(imgLink);
      wrapper.appendChild(imgWrapper);

      /* Info area */
      const info = document.createElement('div');
      info.className = `product-card__info text-${pcardContentAlignment}`;

      const h3 = document.createElement('h3');
      h3.className = 'product-card__title text-pcard-title';

      const titleLink = document.createElement('a');
      let titleClass = 'bm-printer-title-link block';
      if (pcardTitleLineLimit && pcardTitleLineLimit !== 'none') {
        titleClass += ` text-limit-${pcardTitleLineLimit}`;
      }
      titleLink.className = titleClass;
      titleLink.href = model.url;

      const titleSpan = document.createElement('span');
      titleSpan.textContent = model.title;

      titleLink.appendChild(titleSpan);
      h3.appendChild(titleLink);
      info.appendChild(h3);

      const listActions = document.createElement('div');
      listActions.className = 'product-card__list-actions hidden gap-3 flex-col';
      info.appendChild(listActions);

      wrapper.appendChild(info);

      card.appendChild(wrapper);
      col.appendChild(card);
      return col;
    }

    function updateCount() {
      const filtered = getFilteredModels();
      if (countEl) countEl.textContent = `Viewing ${filtered.length} item(s)`;
    }

    function loadMore() {
      if (loadingMore) return;
      if (shownCount >= getFilteredModels().length) return;
      loadingMore = true;
      renderGrid(true);
      loadingMore = false;
    }

    /* ── Event listeners ───────────────────────────────────── */

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        searchQuery = this.value;
        shownCount = 0;
        renderGrid();
        updateCount();
      });
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        sortMode = this.value || 'relevance';
        shownCount = 0;
        renderGrid();
        updateCount();
      });
    }

    var hasProductsCheckbox = section.querySelector('[data-bm-has-products]');
    if (hasProductsCheckbox) {
      hasProductsCheckbox.addEventListener('change', function () {
        onlyWithProducts = this.checked;
        shownCount = 0;
        renderFamilyList();
        renderGrid();
        updateCount();
      });
    }

    viewButtons.forEach((btn) => {
      btn.addEventListener('click', function () {
        isListView = btn.dataset.bmView === 'list';
        viewButtons.forEach((b) => b.classList.remove('btn--active'));
        btn.classList.add('btn--active');
        shownCount = 0;
        renderGrid();
      });
    });

    if (sentinel) {
      const observer = new IntersectionObserver(
        (entries) => { if (entries[0] && entries[0].isIntersecting) loadMore(); },
        { root: null, rootMargin: '400px 0px', threshold: 0 }
      );
      observer.observe(sentinel);
    }

    /* ── Fetch catalog data ────────────────────────────────── */

    const jsonUrl = getCatalogUrl(brandHandle);
    console.log('[brand-modern] fetching:', jsonUrl);

    fetch(jsonUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const processed = processBrandData(data);
        allFamilies = processed.families;
        allModels   = processed.models;
        console.log('[brand-modern] loaded', allFamilies.length, 'families,', allModels.length, 'models');
        renderFamilyList();
        renderGrid();
        updateCount();
      })
      .catch((err) => {
        console.warn('[brand-modern] load failed:', err);
        familyListEl.innerHTML =
          '<li class="brand-modern__error">Unable to load printer families.</li>';
        gridEl.innerHTML =
          '<p class="brand-modern__error">Unable to load printers. Please refresh the page.</p>';
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
